/**
 * Drop per-path ignore lists from a config that is about to travel.
 * For Media Devoted | Created By Ciro Autiero
 *
 * `formula` and `excludeRules` are treated as a provider-agnostic RECIPE that
 * gets copy-pasted between campaigns. An ignore list is the opposite of
 * portable: it names bank rows of ONE campaign's flow, so pasting it elsewhere
 * would silently skip whichever unrelated paths happen to share those ids —
 * or, more likely, skip nothing while looking like it does.
 */

import type {
  ExcludeRulesTree,
  FormulaConfig,
  FormulaGroup,
  RuleNode,
} from '@trafficpulse/shared';

/** Recursively drop the field from a rule tree, preserving everything else. */
export function stripRuleIgnores(node: RuleNode | null): RuleNode | null {
  if (!node) return null;
  if (node.kind === 'not') {
    return { ...node, child: stripRuleIgnores(node.child) ?? node.child };
  }
  if (node.kind !== 'group') return node;
  const { ignoredPathBankIds: _dropped, ...rest } = node;
  return {
    ...rest,
    children: node.children.map((c) => stripRuleIgnores(c) ?? c),
  };
}

export function stripTreeIgnores(rules: ExcludeRulesTree): ExcludeRulesTree {
  return {
    paths: stripRuleIgnores(rules.paths),
    landers: stripRuleIgnores(rules.landers),
    offers: stripRuleIgnores(rules.offers),
  };
}

const stripGroup = (g: FormulaGroup): FormulaGroup => {
  const { ignoredPathBankIds: _dropped, ...rest } = g;
  return rest;
};

export function stripFormulaIgnores(formula: FormulaConfig): FormulaConfig {
  return { ...formula, groups: (formula.groups ?? []).map(stripGroup) };
}
